import type { PnLPoint } from "@gen/tracker/v1/dashboard_pb";

import { aggregate, type Bag } from "@/lib/campaigns-table";
import { statsRowToNumbers, type ReportingSchema } from "@/lib/services/reporting-schema-service";

/**
 * «Сегодня» from one hourly trend over yesterday and today (доска Today).
 * Only BASE measures are summed here; every rate is re-derived through the
 * registry's formulas (`aggregate` → `evaluateFormulas`), so the screen holds
 * no formula of its own. Pure.
 */
export interface TodayModel {
  /** Today so far. */
  today: Bag;
  /** Yesterday up to the same moment of the day — what «к вчера» compares with. */
  yesterdayByNow: Bag;
  /** Running totals per hour, 0…23: today up to the current hour, yesterday whole. */
  runToday: Bag[];
  runYesterday: Bag[];
}

/** "2026-10-14 13:00" → { day, hour }. */
function bucket(label: string): { day: string; hour: number } | undefined {
  const m = /^(\d{4}-\d{2}-\d{2}) (\d{2}):00$/.exec(label);
  return m ? { day: m[1]!, hour: Number(m[2]) } : undefined;
}

/**
 * @param now the current hour and minute in the workspace zone
 */
export function buildToday(
  points: PnLPoint[],
  schema: ReportingSchema,
  days: { today: string; yesterday: string },
  now: { hour: number; minute: number },
): TodayModel {
  const byDay: Record<string, Bag[]> = { [days.today]: [], [days.yesterday]: [] };
  for (const p of points) {
    const b = bucket(p.date);
    if (!b || !(b.day in byDay)) continue;
    byDay[b.day]![b.hour] = statsRowToNumbers(p.stats);
  }
  const hoursToday = byDay[days.today]!;
  const hoursYesterday = byDay[days.yesterday]!;

  const run = (hours: Bag[], last: number): Bag[] => {
    const out: Bag[] = [];
    const acc: Bag[] = [];
    for (let h = 0; h <= last; h++) {
      if (hours[h]) acc.push(hours[h]!);
      out.push(aggregate(acc, schema));
    }
    return out;
  };

  // Yesterday by now: the whole hours before this one, and this hour's share
  // by the minute — 14:02 compares with yesterday's 00:00–14:02, not –15:00.
  const part = now.minute / 60;
  const before = hoursYesterday.slice(0, now.hour).filter(Boolean);
  const current = hoursYesterday[now.hour];
  const partial: Bag | undefined = current ? Object.fromEntries(Object.entries(current).map(([k, v]) => [k, v * part])) : undefined;

  return {
    today: aggregate(hoursToday.filter(Boolean), schema),
    yesterdayByNow: aggregate(partial ? [...before, partial] : before, schema),
    runToday: run(hoursToday, now.hour),
    runYesterday: run(hoursYesterday, 23),
  };
}

/** The hour and minute of `now` in `tz`. Pure. */
export function clockIn(now: Date, tz: string): { hour: number; minute: number } {
  try {
    const parts = new Intl.DateTimeFormat("en-GB", { timeZone: tz, hour: "2-digit", minute: "2-digit", hour12: false }).formatToParts(now);
    const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
    return { hour: get("hour") % 24, minute: get("minute") };
  } catch {
    return { hour: now.getHours(), minute: now.getMinutes() };
  }
}
